/**
 * Tide/water-level display math (spec 12). Pure, no DOM — the sjøkartnull
 * (chart datum) composition line in the Vær-sheet, and the water-level
 * extrema derivation used by the API's tide proxy.
 */

/**
 * Actual water depth now = charted depth (relative to sjøkartnull) + the
 * current water level (cm above sjøkartnull, refcode=cd). A negative
 * `waterLevelCm` (spring low, below chart datum) makes the actual depth
 * *less* than the charted figure — this is deliberately not clamped to the
 * charted value, so a very negative water level can show less water than
 * charted, which is the correct (conservative) reading.
 */
export function actualDepthM(chartedM: number, waterLevelCm: number): number {
  return chartedM + waterLevelCm / 100;
}

export interface TideSample {
  ts: number;
  cm: number;
}

export interface TideExtreme {
  ts: number;
  cm: number;
}

/**
 * Local maxima/minima of a water-level series (Kartverket's prediction
 * curve doesn't carry explicit high/low markers at `datatype=all`, so the
 * proxy derives them itself). A sample is an extreme when both neighbours
 * are on the same side (strictly lower for a high, strictly higher for a
 * low); flat runs are ignored (kept as neither) so a plateau doesn't
 * produce a false extreme at every sample.
 */
export function findExtrema(series: TideSample[]): { highs: TideExtreme[]; lows: TideExtreme[] } {
  const highs: TideExtreme[] = [];
  const lows: TideExtreme[] = [];

  for (let i = 1; i < series.length - 1; i++) {
    const prev = series[i - 1]!;
    const cur = series[i]!;
    const next = series[i + 1]!;
    if (cur.cm > prev.cm && cur.cm > next.cm) highs.push({ ts: cur.ts, cm: cur.cm });
    else if (cur.cm < prev.cm && cur.cm < next.cm) lows.push({ ts: cur.ts, cm: cur.cm });
  }

  return { highs, lows };
}

/** First high/low strictly after `afterMs` (e.g. "now") — null if none found. */
export function nextExtreme(extremes: TideExtreme[], afterMs: number): TideExtreme | null {
  return extremes.filter((e) => e.ts > afterMs).sort((a, b) => a.ts - b.ts)[0] ?? null;
}

/**
 * The series sample closest in time to `atMs` — used for the sheet's
 * "Vannstand nå" headline. Ties resolve to the earlier sample. Null for an
 * empty series (the caller hides the headline rather than showing a bogus 0).
 */
export function nearestWaterLevel(series: TideSample[], atMs: number): TideSample | null {
  if (series.length === 0) return null;
  return series.reduce((best, cur) =>
    Math.abs(cur.ts - atMs) < Math.abs(best.ts - atMs) ? cur : best,
  );
}
